/**
 * TCR-1845 — permission gate dùng CHUNG cho 4 màn catalogue con của "Sản phẩm"
 * (Danh mục / Thương hiệu / Thuộc tính / Đơn vị tính).
 *
 * Vì sao phải là constant chung: gate này nằm ở HAI nơi — menu sidebar
 * (`menuItems.tsx`) quyết định item có hiện không, và route (`App.tsx`) quyết
 * định vào được trang hay ra 403. Hai chỗ đó đã trôi khỏi nhau 3 vòng retest
 * liên tiếp: vòng trước sửa menu nhưng quên route, kết quả là menu hiện đầy đủ
 * còn bấm vào thì trang nào cũng 403. Một nguồn sự thật duy nhất để không lặp lại.
 *
 * Vì sao có 'products.create'/'products.update' trong gate: 3 key
 * 'categories.view'/'brands.view'/'attributes.view' KHÔNG tồn tại trong
 * permission catalogue thật (chỉ owner qua wildcard '*' mới pass), nên không
 * role thường nào bật được chúng — kể cả role được cấp quyền tạo/sửa sản phẩm,
 * thứ mà 4 màn này phục vụ trực tiếp (chọn danh mục/thương hiệu/thuộc tính/đơn
 * vị tính lúc tạo/sửa SP). Giữ lại 3 key cũ trong danh sách để owner và mọi role
 * đang được cấp explicit không bị mất quyền.
 *
 * Ngữ nghĩa là OR: `hasPermission(gate)` ở menu và `PrivateRoute` (qua `hasAny`)
 * đều pass khi khớp BẤT KỲ key nào.
 *
 * Giới hạn đã biết: menu còn bọc ngoài bằng `hasPermission('products.view')`
 * (phải vào được nhóm "Sản phẩm" mới thấy item con), nhưng `PrivateRoute` chỉ
 * diễn tả được OR nên route KHÔNG kèm điều kiện AND đó. Role có
 * 'products.create' mà thiếu 'products.view' sẽ vào được trang rồi nhận 403 từ
 * API (BE gate mọi endpoint catalogue bằng 'products.view'). Trường hợp này nằm
 * ngoài phạm vi TCR-1845 nên không mở rộng `PrivateRoute` ở đây.
 */
const PRODUCT_WRITE_PERMISSIONS = ['products.create', 'products.update'] as const;

export const CATEGORY_GATE = ['categories.view', ...PRODUCT_WRITE_PERMISSIONS];
export const BRAND_GATE = ['brands.view', ...PRODUCT_WRITE_PERMISSIONS];
export const ATTRIBUTE_GATE = ['attributes.view', ...PRODUCT_WRITE_PERMISSIONS];

/**
 * Đơn vị tính chưa từng có key 'units.view' riêng — trước TCR-1845 route gate
 * CỨNG bằng 'products.update', nên role chỉ có quyền TẠO sản phẩm không chọn
 * được đơn vị tính lúc tạo mới.
 */
export const UNIT_GATE = [...PRODUCT_WRITE_PERMISSIONS];

/**
 * Ai tạo HOẶC sửa được sản phẩm thì toàn quyền thêm/sửa/xoá catalogue.
 *
 * Trước TCR-1845 vòng 6, gate map 1-1 hành động ↔ quyền: Thêm←products.create,
 * Sửa←products.update, Xoá←products.delete. QA báo fail đúng hệ quả đó — role
 * chỉ được cấp "Tạo sản phẩm" thì ở màn Danh mục/Thương hiệu/Thuộc tính chỉ
 * thấy mỗi nút Thêm; role chỉ có "Sửa sản phẩm" thì chỉ thấy Sửa. Chốt lại
 * 18/09/2026: 5 màn catalogue là dữ liệu phụ trợ để chọn lúc tạo/sửa sản phẩm,
 * nên quyền quản trị chúng đi theo "được tạo hoặc sửa sản phẩm".
 *
 * BE đã nới tương ứng trong `catalogue-permissions.ts` (POST/PUT/PATCH nhận
 * products.create|products.update; DELETE nhận thêm products.delete). Hai bên
 * PHẢI khớp — lệch là nút hiện ra rồi bấm vào 403, đúng vết xe đổ vòng 1→2.
 * `productCatalogueGates.test.ts` đọc thẳng file BE để chặn lệch.
 *
 * Key cũ của từng màn ('categories.create'…) giữ lại cho role nào đang được cấp
 * explicit trong `roles.permission_keys` ở DB không bị mất quyền.
 */
export const CATALOGUE_CREATE_GATE = [
    'categories.create',
    'brands.create',
    'attributes.create',
    ...PRODUCT_WRITE_PERMISSIONS,
];

export const CATALOGUE_UPDATE_GATE = [
    'categories.update',
    'brands.update',
    'attributes.update',
    ...PRODUCT_WRITE_PERMISSIONS,
];

/**
 * Nhánh xoá giữ thêm 'products.delete' ("Ẩn sản phẩm") để role nào đang CHỈ
 * được cấp quyền đó không mất quyền xoá catalogue vốn có trước ticket này.
 */
export const CATALOGUE_DELETE_GATE = [
    'categories.delete',
    'brands.delete',
    'attributes.delete',
    ...PRODUCT_WRITE_PERMISSIONS,
    'products.delete',
];
